import React, { useEffect, useState } from 'react';
import { Button } from './ui/Button';
import { Decisions, GameState, GAME_CONSTANTS } from '../types/game';
import { ArrowRightIcon, MinusIcon, PlusIcon } from 'lucide-react';
interface DecisionsPanelProps {
  gameState: GameState;
  onCommit: (decisions: Decisions) => void;
}
function SectionLabel({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="flex items-center gap-2 mb-3" data-id="element-90">
      <div className="w-1 h-4 rounded-full bg-[#00D4C8]" data-id="element-91" />
      <span className="text-xs font-semibold text-[#F0F0F0] uppercase tracking-widest" data-id="element-92">
        {children}
      </span>
    </div>;
}
function Stepper({
  label,
  value,
  onDecrement,
  onIncrement,
  accent = '#00D4C8'
}: {
  label: string;
  value: number;
  onDecrement: () => void;
  onIncrement: () => void;
  accent?: string;
}) {
  return <div className="bg-[#1A1A1E] border border-[#2A2A32] rounded-lg p-4" data-id="element-93">
      <p className="text-xs text-[#6B6B7E] uppercase tracking-widest mb-3" data-id="element-94">
        {label}
      </p>
      <div className="flex items-center justify-between" data-id="element-95">
        <button onClick={onDecrement} className="w-8 h-8 flex items-center justify-center rounded-md bg-[#2A2A32] hover:bg-[#3A3A44] text-[#F0F0F0] transition-colors" data-id="element-96">
          <MinusIcon className="w-3.5 h-3.5" data-id="element-97" />
        </button>
        <span className="font-mono text-2xl font-bold" style={{
        color: accent
      }} data-id="element-98">
          {value}
        </span>
        <button onClick={onIncrement} className="w-8 h-8 flex items-center justify-center rounded-md text-[#0E0E10] transition-all hover:opacity-90" style={{
        backgroundColor: accent
      }} data-id="element-99">
          <PlusIcon className="w-3.5 h-3.5" data-id="element-100" />
        </button>
      </div>
    </div>;
}
export function DecisionsPanel({
  gameState,
  onCommit
}: DecisionsPanelProps) {
  const [unitPrice, setUnitPrice] = useState(100);
  const [newEngineers, setNewEngineers] = useState(0);
  const [newSales, setNewSales] = useState(0);
  const [salaryMultiplier, setSalaryMultiplier] = useState(1.0);
  const [projectedCash, setProjectedCash] = useState(0);
  const [projectedRevenue, setProjectedRevenue] = useState(0);
  const [projectedBurn, setProjectedBurn] = useState(0);
  useEffect(() => {
    const totalEngineers = gameState.engineers + newEngineers;
    const totalSales = gameState.sales + newSales;
    const engineerCost = totalEngineers * GAME_CONSTANTS.BASE_SALARY_ENGINEER * salaryMultiplier;
    const salesCost = totalSales * GAME_CONSTANTS.BASE_SALARY_SALES * salaryMultiplier;
    const operatingCost = GAME_CONSTANTS.BASE_OPERATING_COST + (totalEngineers + totalSales) * 1000;
    const expenses = engineerCost + salesCost + operatingCost;
    const priceFactor = Math.max(0.1, 200 / unitPrice);
    const qualityFactor = gameState.productQuality / 50;
    const demand = GAME_CONSTANTS.MARKET_DEMAND_BASE * priceFactor * qualityFactor;
    const capacity = totalSales * GAME_CONSTANTS.SALES_CAPACITY_PER_REP * salaryMultiplier;
    const salesUnits = Math.min(demand, capacity);
    const revenue = salesUnits * unitPrice;
    setProjectedRevenue(revenue);
    setProjectedBurn(expenses);
    setProjectedCash(gameState.cash + revenue - expenses);
  }, [unitPrice, newEngineers, newSales, salaryMultiplier, gameState]);
  const fmt = (val: number) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
    notation: 'compact'
  }).format(val);
  const attritionLevel = salaryMultiplier < 0.8 ? {
    label: 'HIGH',
    color: '#EF4444'
  } : salaryMultiplier < 1.0 ? {
    label: 'MODERATE',
    color: '#F5A623'
  } : salaryMultiplier < 1.2 ? {
    label: 'LOW',
    color: '#22C55E'
  } : {
    label: 'MINIMAL',
    color: '#00D4C8'
  };
  const quarter = (gameState.turn - 1) % 4 + 1;
  return <div className="bg-[#16161A] border border-[#2A2A32] rounded-lg p-5 h-full flex flex-col" data-id="element-101">
      {/* Header */}
      <div className="flex items-center justify-between mb-5" data-id="element-102">
        <div data-id="element-103">
          <h2 className="text-sm font-semibold text-[#F0F0F0] tracking-wide" data-id="element-104">
            Quarterly Decisions
          </h2>
          <p className="text-xs text-[#6B6B7E] mt-0.5" data-id="element-105">
            Q{quarter} · Set your strategy
          </p>
        </div>
        <span className="font-mono text-xs text-[#00D4C8] bg-[#00D4C8]/10 border border-[#00D4C8]/20 px-2.5 py-1 rounded-full" data-id="element-106">
          PLANNING
        </span>
      </div>

      <div className="flex-1 overflow-y-auto space-y-5 pr-1" data-id="element-107">
        {/* Pricing */}
        <div data-id="element-108">
          <SectionLabel data-id="element-109">Unit Price</SectionLabel>
          <div className="bg-[#1A1A1E] border border-[#2A2A32] rounded-lg p-4" data-id="element-110">
            <div className="flex items-center justify-between mb-3" data-id="element-111">
              <span className="text-xs text-[#6B6B7E]" data-id="element-112">
                Price per seat / month
              </span>
              <span className="font-mono font-bold text-lg text-[#00D4C8]" data-id="element-113">
                ${unitPrice}
              </span>
            </div>
            <input type="range" min={10} max={500} step={5} value={unitPrice} onChange={e => setUnitPrice(Number(e.target.value))} className="w-full h-1.5 rounded-full appearance-none cursor-pointer" style={{
            background: `linear-gradient(to right, #00D4C8 0%, #00D4C8 ${(unitPrice - 10) / 490 * 100}%, #2A2A32 ${(unitPrice - 10) / 490 * 100}%, #2A2A32 100%)`,
            accentColor: '#00D4C8'
          }} data-id="element-114" />
            <div className="flex justify-between text-[10px] text-[#6B6B7E] mt-1.5" data-id="element-115">
              <span data-id="element-116">$10</span>
              <span className="text-[#6B6B7E]/60" data-id="element-117">
                Proj. Revenue:{' '}
                <span className="text-[#22C55E]" data-id="element-118">{fmt(projectedRevenue)}</span>
              </span>
              <span data-id="element-119">$500</span>
            </div>
          </div>
        </div>

        {/* Hiring */}
        <div data-id="element-120">
          <SectionLabel data-id="element-121">New Hires</SectionLabel>
          <div className="grid grid-cols-2 gap-3" data-id="element-122">
            <Stepper label="Engineers" value={newEngineers} onDecrement={() => setNewEngineers(Math.max(0, newEngineers - 1))} onIncrement={() => setNewEngineers(newEngineers + 1)} accent="#00D4C8" data-id="element-123" />
            <Stepper label="Sales Reps" value={newSales} onDecrement={() => setNewSales(Math.max(0, newSales - 1))} onIncrement={() => setNewSales(newSales + 1)} accent="#F5A623" data-id="element-124" />
          </div>
          <p className="text-xs text-[#6B6B7E] mt-2 px-1" data-id="element-125">
            Updated payroll:{' '}
            <span className="text-[#F0F0F0] font-mono" data-id="element-126">
              {fmt(projectedBurn)}
            </span>{' '}
            / qtr
          </p>
        </div>

        {/* Salary */}
        <div data-id="element-127">
          <SectionLabel data-id="element-128">Salary vs Market</SectionLabel>
          <div className="bg-[#1A1A1E] border border-[#2A2A32] rounded-lg p-4" data-id="element-129">
            <div className="flex items-center justify-between mb-3" data-id="element-130">
              <span className="text-xs text-[#6B6B7E]" data-id="element-131">
                Industry average multiplier
              </span>
              <span className="font-mono font-bold text-lg text-[#00D4C8]" data-id="element-132">
                {Math.round(salaryMultiplier * 100)}%
              </span>
            </div>
            <input type="range" min={0.6} max={1.4} step={0.05} value={salaryMultiplier} onChange={e => setSalaryMultiplier(Number(e.target.value))} className="w-full h-1.5 rounded-full appearance-none cursor-pointer" style={{
            background: `linear-gradient(to right, #00D4C8 0%, #00D4C8 ${(salaryMultiplier - 0.6) / 0.8 * 100}%, #2A2A32 ${(salaryMultiplier - 0.6) / 0.8 * 100}%, #2A2A32 100%)`,
            accentColor: '#00D4C8'
          }} data-id="element-133" />
            <div className="flex items-center justify-between mt-2" data-id="element-134">
              <span className="text-[10px] text-[#6B6B7E]" data-id="element-135">60%</span>
              <div className="flex items-center gap-1.5" data-id="element-136">
                <span className="text-xs text-[#6B6B7E]" data-id="element-137">Attrition:</span>
                <span className="text-xs font-semibold font-mono" style={{
                color: attritionLevel.color
              }} data-id="element-138">
                  {attritionLevel.label}
                </span>
              </div>
              <span className="text-[10px] text-[#6B6B7E]" data-id="element-139">140%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Summary + CTA */}
      <div className="mt-5 pt-5 border-t border-[#2A2A32]" data-id="element-140">
        <div className="bg-[#1A1A1E] border border-[#2A2A32] rounded-lg p-4 mb-4" data-id="element-141">
          <div className="flex items-center justify-between" data-id="element-142">
            <span className="text-xs text-[#6B6B7E] uppercase tracking-widest" data-id="element-143">
              Proj. End-of-Qtr Cash
            </span>
            <span className="font-mono font-bold text-xl" style={{
            color: projectedCash < 0 ? '#EF4444' : '#22C55E'
          }} data-id="element-144">
              {fmt(projectedCash)}
            </span>
          </div>
          <div className="flex items-center gap-4 mt-2 text-xs text-[#6B6B7E]" data-id="element-145">
            <span data-id="element-146">
              Rev{' '}
              <span className="text-[#22C55E] font-mono" data-id="element-147">
                {fmt(projectedRevenue)}
              </span>
            </span>
            <span data-id="element-148">·</span>
            <span data-id="element-149">
              Costs{' '}
              <span className="text-[#EF4444] font-mono" data-id="element-150">
                -{fmt(projectedBurn)}
              </span>
            </span>
          </div>
        </div>

        <Button fullWidth size="lg" onClick={() => onCommit({
        unitPrice,
        newEngineers,
        newSales,
        salaryMultiplier
      })} className="group font-semibold tracking-wide" data-id="element-151">
          Advance to Q{quarter + 1}
          <ArrowRightIcon className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-152" />
        </Button>
      </div>
    </div>;
}